import { Button, Card, Divider, Group, Modal, Stack, Switch, Tabs, Text, Badge } from '@mantine/core'
import { Client } from '@/api/controlUnitApi/client'
import { Route } from '@/routes/hardware.$source.$tab'
import { IconRefresh } from '@tabler/icons-react'
import IconButton from '../Common/IconButton'
import { Fragment, ReactNode, useState } from 'react'
import { PiSkull } from 'react-icons/pi'
import { useDisclosure } from '@mantine/hooks'
import useRestartsPanelData, { RestartsDeviceType } from './useRestartsPanelData'
import { Camera3dPosition, CameraPosition } from '@/api/controlUnitApi/types.gen'
import { noop } from 'lodash-es'
import FunctionUnavailable from '../Common/FunctionUnavailable'
import { useTranslation } from 'react-i18next'

const StackItem = ({
  label,
  icon,
  onToggle,
  hideRestart,
  tooltip,
  onRestart,
  checked,
  isLoading,
  statusBadge,
}: {
  label: string | ReactNode
  icon: ReactNode
  onToggle: (bool: boolean) => void
  tooltip: string
  onRestart: () => void
  checked: boolean
  hideRestart?: boolean
  isLoading?: boolean
  statusBadge?: ReactNode
}) => {
  const { t } = useTranslation('hardware')
  return (
    <Group justify="space-between" align="center">
      <Group align="center">
        {typeof label === 'string' ? <Text> {label} </Text> : label}
        {statusBadge}
      </Group>
      <Group>
        <Switch
          size="lg"
          onLabel="ON"
          offLabel="OFF"
          onChange={e => onToggle(e.target.checked)}
          checked={checked}
          disabled={isLoading}
        />
        {!hideRestart && (
          <IconButton
            label={isLoading ? `${tooltip} - ${t('deviceBusy')}` : tooltip}
            onClick={onRestart}
            icon={icon}
            disabled={isLoading}
          />
        )}
      </Group>
    </Group>
  )
}

const ServerStackItem = ({ label, actions }: { label: string | ReactNode; actions: ReactNode }) => {
  return (
    <>
      <Text> {label} </Text>
      {actions}
    </>
  )
}

enum Action {
  SHUT_DOWN = 'SHUT DOWN',
  FORCE_SHUT_DOWN = 'FORCE SHUT DOWN',
  RESTART = 'RESTART',
  FORCE_RESTART = 'FORCE RESTART',
}

const RestartsPanel = ({ client }: { client: Client }) => {
  const { source } = Route.useParams()
  const [isModalOpened, { open, close }] = useDisclosure(false)
  const [action, setAction] = useState<Action | null>(null)
  const [target, setTarget] = useState<'kit' | 'server'>('kit')
  const { t } = useTranslation(['hardware', 'common'])

  const {
    gpsState,
    cameraFlState,
    cameraFcState,
    cameraFrState,
    camera3dFlState,
    camera3dFrState,
    beaconState,
    isTogglingBeacon,
    cameraToggleStates,
    camera3dToggleStates,
    isDeviceAvailable,
    toggleCamera,
    toggleCamera3d,
    restartCamera3d,
    restartCamera,
    restartGps,
    toggleGps,
    restartServer,
    forceRestartServer,
    shutdownServer,
    forceShotDownServer,
    shutdownKit,
    forceShotDownKit,
    restartKit,
    forceRestartKit,
    toggleBeacon,
  } = useRestartsPanelData({
    client,
    source,
  })

  const handleAction = (target: 'kit' | 'server') => (action: Action) => {
    setTarget(target)
    setAction(action)
    open()
  }

  const handleServerAction = (action: Action) => {
    handleAction('server')(action)
  }

  const handleKitAction = (action: Action) => {
    handleAction('kit')(action)
  }

  const getActionTranslation = (action: Action) => {
    switch (action) {
      case Action.SHUT_DOWN:
        return t('shutDown')
      case Action.FORCE_SHUT_DOWN:
        return t('forceShutDown')
      case Action.RESTART:
        return t('restart')
      case Action.FORCE_RESTART:
        return t('forceRestart')
      default:
        return ''
    }
  }

  const getTargetTranslation = (target: 'kit' | 'server') => {
    return target === 'kit' ? t('kit') : t('server')
  }

  const serverActions = (
    <Group>
      <FunctionUnavailable>
        <Button onClick={() => handleServerAction(Action.SHUT_DOWN)} disabled color="red">
          {t('shutDown')}
        </Button>
      </FunctionUnavailable>
      <FunctionUnavailable>
        <Button
          leftSection={<PiSkull size={16} />}
          onClick={() => handleServerAction(Action.FORCE_SHUT_DOWN)}
          disabled
          color="red"
        >
          {t('forceShutDown')}
        </Button>
      </FunctionUnavailable>
      <FunctionUnavailable>
        <Button onClick={() => handleServerAction(Action.RESTART)} disabled>
          {t('restart')}
        </Button>
      </FunctionUnavailable>
      <Button onClick={() => handleServerAction(Action.FORCE_RESTART)} disabled={!isDeviceAvailable('server')}>
        {t('forceRestart')}
      </Button>
    </Group>
  )

  const kitActions = (
    <Group>
      <Button onClick={() => handleKitAction(Action.SHUT_DOWN)} color="red">
        {t('shutDown')}
      </Button>
      <Button leftSection={<PiSkull size={16} />} onClick={() => handleKitAction(Action.FORCE_SHUT_DOWN)} color="red">
        {t('forceShutDown')}
      </Button>
      <FunctionUnavailable>
        <Button onClick={() => handleKitAction(Action.RESTART)} disabled>
          {t('restart')}
        </Button>
      </FunctionUnavailable>
      <Button onClick={() => handleKitAction(Action.FORCE_RESTART)}>{t('forceRestart')}</Button>
    </Group>
  )

  const determineModalConfirmAction = (action: Action, target: 'server' | 'kit') => {
    switch (`${action}_${target}`) {
      case `${Action.SHUT_DOWN}_server`:
        return () => shutdownServer({})
      case `${Action.FORCE_SHUT_DOWN}_server`:
        return () => forceShotDownServer({})
      case `${Action.RESTART}_server`:
        return () => restartServer({})
      case `${Action.FORCE_RESTART}_server`:
        return () => forceRestartServer({})
      case `${Action.SHUT_DOWN}_kit`:
        return () => shutdownKit({})
      case `${Action.FORCE_SHUT_DOWN}_kit`:
        return () => forceShotDownKit({})
      case `${Action.RESTART}_kit`:
        return () => restartKit({})
      case `${Action.FORCE_RESTART}_kit`:
        return () => forceRestartKit({})
      default:
        return () => {}
    }
  }

  const cameras: {
    labelKey: 'frontLeftCamera' | 'frontCenterCamera' | 'frontRightCamera' | 'frontLeft3dCamera' | 'frontRight3dCamera'
    position: CameraPosition
    is3d: boolean
    deviceType: RestartsDeviceType
  }[] = [
    {
      labelKey: 'frontLeftCamera',
      position: 'FL',
      is3d: false,
      deviceType: 'cameraFL',
    },
    {
      labelKey: 'frontCenterCamera',
      position: 'FC',
      is3d: false,
      deviceType: 'cameraFC',
    },
    {
      labelKey: 'frontRightCamera',
      position: 'FR',
      is3d: false,
      deviceType: 'cameraFR',
    },
    {
      labelKey: 'frontLeft3dCamera',
      position: 'FL',
      is3d: true,
      deviceType: 'camera3dFL',
    },
    {
      labelKey: 'frontRight3dCamera',
      position: 'FR',
      is3d: true,
      deviceType: 'camera3dFR',
    },
  ]

  const getCameraState = (position: CameraPosition, is3d: boolean) => {
    switch (position) {
      case 'FL':
        return is3d ? camera3dFlState : cameraFlState
      case 'FC':
        return cameraFcState
      case 'FR':
        return is3d ? camera3dFrState : cameraFrState
      default:
        return null
    }
  }

  return (
    <>
      <Tabs.Panel value="restarts">
        <Card shadow="sm" p="lg" radius="md" withBorder mt="lg">
          <Stack gap="md">
            <StackItem
              label={t('gps')}
              icon={<IconRefresh />}
              tooltip={t('restartGps')}
              checked={gpsState?.enabled ?? false}
              onToggle={bool => toggleGps({ body: { enabled: bool } })}
              isLoading={!isDeviceAvailable('gps')}
              onRestart={() => restartGps({})}
              statusBadge={
                !isDeviceAvailable('gps') ? (
                  <Badge color="blue" variant="light" size="sm">
                    {t('restarting')}
                  </Badge>
                ) : undefined
              }
            />
            <Divider />
            {cameras.map(({ labelKey, position, is3d, deviceType }) => {
              const camLabel = t(labelKey)
              return (
                <Fragment key={`${position}-${is3d}`}>
                  <StackItem
                    label={camLabel}
                    icon={<IconRefresh />}
                    onToggle={enabled =>
                      is3d
                        ? toggleCamera3d({
                            path: { camera_position: position as Camera3dPosition },
                            body: { enabled },
                          })
                        : toggleCamera({ path: { camera_position: position }, body: { enabled } })
                    }
                    tooltip={t('restartDevice', { device: camLabel })}
                    checked={getCameraState(position, is3d)?.enabled ?? false}
                    onRestart={() =>
                      is3d
                        ? restartCamera3d({ path: { camera_position: position as Camera3dPosition } })
                        : restartCamera({ path: { camera_position: position } })
                    }
                    isLoading={
                      !isDeviceAvailable(deviceType) ||
                      cameraToggleStates.get(position) ||
                      camera3dToggleStates.get(position)
                    }
                    statusBadge={
                      !isDeviceAvailable(deviceType) ? (
                        <Badge color="blue" variant="light" size="sm">
                          {t('restarting')}
                        </Badge>
                      ) : undefined
                    }
                  />
                  <Divider />
                </Fragment>
              )
            })}
            <StackItem
              label={t('beacon')}
              icon={<IconRefresh />}
              onToggle={bool => toggleBeacon({ body: { enabled: bool } })}
              tooltip={t('restartBeacon')}
              hideRestart
              checked={beaconState?.enabled ?? false}
              onRestart={noop}
              isLoading={isTogglingBeacon}
            />
            <Divider />
            <ServerStackItem label={t('server')} actions={serverActions} />
            <Divider />
            <ServerStackItem label={t('kit')} actions={kitActions} />
          </Stack>
        </Card>
      </Tabs.Panel>
      <Modal opened={isModalOpened} onClose={close} title={t('confirmation')} centered zIndex={500}>
        <Text mb="md">
          {action &&
            t('confirmAction', {
              action: getActionTranslation(action),
              target: getTargetTranslation(target),
            })}
        </Text>
        <Button
          onClick={() => {
            if (!action) return
            determineModalConfirmAction(action, target)()
            close()
          }}
        >
          {t('confirm', { ns: 'common' })}
        </Button>
      </Modal>
    </>
  )
}

export default RestartsPanel
